import { useNavigate } from 'react-router-dom'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { notificationsApi } from '../api/notifications'
import { useLanguage } from '../hooks/useLanguage'
import { useNotifications } from '../hooks/useNotifications'
import type { NotificationItem } from '../types/notification'

export function NotificationsPage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { lang, t } = useLanguage()
  const { data, isLoading, error } = useNotifications(lang)

  const handleBack = () => {
    // Notifications can be the first MiniApp route when opened from a Telegram
    // digest. Browser history then has no in-app page to return to. The bell is
    // also a home-level entry, so its stable parent is always the MiniApp home.
    navigate('/', { replace: true })
  }

  const refreshNotifications = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['notifications', 'list'] }),
      queryClient.invalidateQueries({ queryKey: ['notifications', 'unread-count'] }),
    ])
  }

  const readMutation = useMutation({
    mutationFn: (notificationId: string) => notificationsApi.markRead(notificationId),
    onSuccess: refreshNotifications,
  })
  const readAllMutation = useMutation({
    mutationFn: notificationsApi.markAllRead,
    onSuccess: refreshNotifications,
  })

  const openNotification = async (item: NotificationItem) => {
    if (!item.is_read) {
      try {
        await readMutation.mutateAsync(item.notification_id)
      } catch (readError) {
        console.error('Failed to mark notification read:', readError)
      }
    }
    if (item.type === 'prediction_market_move') {
      if (item.polymarket_url) window.open(item.polymarket_url, '_blank', 'noopener,noreferrer')
      return
    }
    navigate(
      `/event/${encodeURIComponent(item.event_id)}?date=${encodeURIComponent(item.date)}&narrative=${encodeURIComponent(item.narrative || '')}&from=notifications`,
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
      <header className="sticky top-0 z-20 border-b border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-900">
        <div className="mx-auto flex max-w-3xl items-center gap-3 px-4 py-3">
          <button
            type="button"
            onClick={handleBack}
            className="flex h-8 w-8 items-center justify-center rounded-md bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-200"
            aria-label={t('返回', 'Back')}
          >
            ←
          </button>
          <div className="min-w-0 flex-1">
            <h1 className="text-base font-semibold text-gray-900 dark:text-white">
              {t('通知', 'Notifications')}
            </h1>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {t(`${data?.unread_count || 0} 条未读`, `${data?.unread_count || 0} unread`)}
            </p>
          </div>
          {(data?.unread_count || 0) > 0 && (
            <button
              type="button"
              disabled={readAllMutation.isPending}
              onClick={() => readAllMutation.mutate()}
              className="text-xs font-medium text-blue-600 disabled:opacity-50 dark:text-blue-300"
            >
              {t('全部已读', 'Mark all read')}
            </button>
          )}
        </div>
      </header>

      <main className="mx-auto max-w-3xl space-y-3 p-4">
        {isLoading && (
          <div className="py-12 text-center text-sm text-gray-500 dark:text-gray-400">
            {t('加载中...', 'Loading...')}
          </div>
        )}
        {error && (
          <div className="py-12 text-center text-sm text-red-500">
            {t('通知加载失败，请稍后重试', 'Failed to load notifications. Please try again.')}
          </div>
        )}
        {!isLoading && !error && (data?.notifications.length || 0) === 0 && (
          <div className="py-12 text-center">
            <div className="text-3xl">🔔</div>
            <p className="mt-3 text-sm font-medium text-gray-900 dark:text-white">
              {t('暂无通知', 'No notifications yet')}
            </p>
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              {t('关注 Token 后，相关的新事件会出现在这里。', 'New events for followed tokens will appear here.')}
            </p>
          </div>
        )}
        {data?.notifications.map((item) => {
          const isPredictionMarket = item.type === 'prediction_market_move'
          const cardColor = isPredictionMarket
            ? item.is_read
              ? 'border-amber-200 bg-amber-50/40 dark:border-amber-900/60 dark:bg-amber-950/20'
              : 'border-amber-300 bg-amber-50 dark:border-amber-700 dark:bg-amber-950/35'
            : item.is_read
              ? 'border-sky-200 bg-sky-50/35 dark:border-sky-900/60 dark:bg-sky-950/20'
              : 'border-sky-300 bg-sky-50 dark:border-sky-700 dark:bg-sky-950/35'
          return (
            <button
            key={item.notification_id}
            type="button"
            onClick={() => void openNotification(item)}
            className={`w-full rounded-lg border p-4 text-left transition-colors ${cardColor}`}
          >
            <div className="flex items-start gap-3">
              <div className="min-w-0 flex-1">
                <div className="mb-2 flex flex-wrap items-center gap-1.5">
                  {item.matched_tokens.map((token) => (
                    <span
                      key={token}
                      className="rounded bg-gray-100 px-2 py-0.5 text-xs font-bold text-gray-800 dark:bg-gray-700 dark:text-gray-100"
                    >
                      {token}
                    </span>
                  ))}
                  <span className={`rounded px-2 py-0.5 text-[11px] font-semibold ${
                    isPredictionMarket
                      ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/60 dark:text-amber-200'
                      : 'bg-sky-100 text-sky-800 dark:bg-sky-900/60 dark:text-sky-200'
                  }`}>
                    {isPredictionMarket
                      ? t('预测市场', 'Prediction Market')
                      : t('Daily Web3 Events', 'Daily Web3 Events')}
                  </span>
                  <span className="ml-auto text-xs text-gray-400">{item.date}</span>
                </div>
                <h2 className="text-sm font-semibold leading-snug text-gray-900 dark:text-white">
                  {item.title}
                </h2>
                {item.type === 'prediction_market_move' && item.changes.slice(0, 3).map((change) => (
                  <div key={`${change.market_id}:${change.outcome}`} className="mt-2 text-xs leading-relaxed text-gray-600 dark:text-gray-300">
                    <div>{change.question} · {change.outcome}</div>
                    <div className={change.change_points > 0 ? 'font-semibold text-green-600' : 'font-semibold text-red-600'}>
                      {t(`${change.window_minutes || 30} 分钟内`, `In ${change.window_minutes || 30} min`)}：{' '}
                      {change.previous_probability}% → {change.current_probability}%
                      {' '}({change.change_points > 0 ? '+' : ''}{change.change_points} {t('个百分点', 'points')})
                    </div>
                  </div>
                ))}
              </div>
              {!item.is_read && (
                <span className="mt-1 h-2 w-2 shrink-0 rounded-full bg-red-500" aria-label={t('未读', 'Unread')} />
              )}
            </div>
            </button>
          )
        })}
      </main>
    </div>
  )
}
